/* eslint-disable @next/next/no-img-element */
'use client'
import { EmptyContent, Image } from '@/components'
import { PAGE_SIZE } from '@/constants'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ProductLoading } from '../../components/product-loading'
import { ProductGrid } from '../../components/products-grid'
import { fetchCustomAttributeMetadata, fetchProducts } from '../../services'
import { ICustomAttributeMetadata } from '../../types'
import { FilterButton } from './components/filter-button'
import ProductFilterTag from './components/product-filter-tag'
import { ProductSort } from './components/product-sort'
import { DescriptionCategory } from './components/description-category'
type Props = {
  checkRouter: IRouterCms | null
  categoryId: string[]
}

export const ProductComponent: React.FC<Props> = ({ checkRouter, categoryId }) => {
  const [paramsObject, setParamsObject] = useState<any>({})
  const [paramsCompiled, setParamsCompiled] = useState<any>([])
  const [savedAggregations, setSavedAggregations] = useState<IAggregations[] | undefined>(undefined)
  const searchParamsClient = useSearchParams()
  const selectedColorFilters = searchParamsClient.get('color')?.split(',').filter(Boolean) || []
  const t = useTranslations()

  useEffect(() => {
    let paramsObjectRef: any = {}
    // if (searchParamsClient?.size == 0) return setParamsObject({})
    searchParamsClient.forEach((value: any, key: any) => {
      paramsObjectRef[key] = value
    })

    setParamsObject(paramsObjectRef)
  }, [searchParamsClient])

  const {
    data: getCustomAttributeMetadata,
    isSuccess: isSuccesCustomAttribute,
    isLoading: isLoadingCustomAttribute,
  } = useQuery({
    queryKey: ['GET_CUSTOM_ATTRIBUTE_KEY', paramsObject],
    queryFn: async (): Promise<ICustomAttributeMetadata[]> => {
      let paramsObjectRef: any = []
      searchParamsClient.forEach((_, key: any) => {
        if (key != 'sort' && key != 'sortBy' && key != 'page' && key != 'search' && key != 'pageSize')
          paramsObjectRef.push({
            attribute_code: key,
            entity_type: 'catalog_product',
          })
      })
      if (paramsObjectRef.length < 1) return []

      const data = await fetchCustomAttributeMetadata({ attributes: [...paramsObjectRef] })
      return data?.filter((item: ICustomAttributeMetadata) => item?.attribute_options?.length > 0)
    },
    staleTime: 5 * 60 * 1000,
  })
  useEffect(() => {
    if (isSuccesCustomAttribute && getCustomAttributeMetadata) {
      const getValuesAttribute = (data: ICustomAttributeMetadata[], paramsObject: any) => {
        if (!data) return []
        let result: any = []
        Object.keys(paramsObject).map((key) => {
          const dataItem: ICustomAttributeMetadata | undefined = data?.find(
            (item: ICustomAttributeMetadata) => item?.attribute_code === key
          )
          if (dataItem) {
            let itemSameLabel = dataItem?.attribute_options
              ?.filter((item) => paramsObject[key]?.split(',').includes(item.label))
              .map((item) => item.value)
            if (itemSameLabel) {
              result = [
                ...result,
                {
                  [dataItem?.attribute_code]: {
                    ['in']: [...itemSameLabel],
                  },
                },
              ]
            }
          }
        })
        return result
      }
      setParamsCompiled(getValuesAttribute(getCustomAttributeMetadata, paramsObject))
    } else {
      setParamsCompiled([])
    }
  }, [getCustomAttributeMetadata, isSuccesCustomAttribute, paramsObject])

  const { search: searchKw = '', price, sort, sortBy, page = 1, name, pageSize, category_uid } = paramsObject
  const getParams = useCallback((): IOptionsProductProps => {
    const filter: any = {}
    let sortValue

    // nếu sort hoặc sortBy không tồn tại và không có filter nào được áp dụng thì mặc định sắp xếp theo POSITION DESC
    if (!sort && !sortBy) {
      if (!paramsCompiled.length && !searchKw) {
        sortValue = {
          position: 'DESC',
        }
      }
    } else {
      // Nếu có sort và sortBy thì gán giá trị tương ứng
      sortValue = {
        [sort]: sortBy === 'ASC' ? 'ASC' : 'DESC',
      }
    }

    if (price) {
      const [from, to] = price.split('-').map(Number)
      filter.price = {
        from,
        to,
      }
    }
    if (name) filter.name = { match: name }
    if (paramsCompiled) {
      paramsCompiled?.map((item: any) => {
        Object.keys(item)?.map((query) => {
          filter[query] = item[query]
        })
      })
    }
    if (categoryId?.length > 0)
      filter['category_uid'] = { in: [...(filter?.category_uid?.in || []), checkRouter?.route?.uid] }
    return {
      filter: filter,
      pageSize: pageSize || PAGE_SIZE,
      currentPage: +page || 1,
      search: searchKw || '',
      sort: sortValue,
    }
  }, [
    categoryId?.length,
    checkRouter?.route?.uid,
    name,
    page,
    paramsCompiled,
    price,
    searchKw,
    sort,
    sortBy,
    pageSize,
  ])

  const {
    data: listProduct,
    isLoading: isPendingListProducts,
    isPlaceholderData: isPlaceholderDataListProducts,
  } = useQuery({
    queryKey: ['GET_LIST_PRODUCT', getParams(), searchParamsClient?.get('search')],
    queryFn: async () => {
      return await fetchProducts(getParams())
    },
    placeholderData: keepPreviousData,
  })

  // Lưu aggregations khi có dữ liệu mới
  useEffect(() => {
    if (listProduct?.aggregations && listProduct.aggregations.length > 0) {
      setSavedAggregations(listProduct.aggregations)
    }
  }, [listProduct?.aggregations])

  const listProductItems = () => {
    if (isPendingListProducts || isLoadingCustomAttribute || isPlaceholderDataListProducts)
      return <ProductLoading />

    if (!listProduct || listProduct?.items?.length < 1 || !listProduct?.items)
      return <EmptyContent title="Không có sản phẩm nào phù hợp" />

    return (
      <ProductGrid
        products={listProduct}
        direction="GRID"
        onPageChange={handleScrollToProductList}
        selectedColorFilters={selectedColorFilters}
      />
    )
  }

  const listProductRef = useRef<HTMLDivElement | null>(null)

  const getHeaderHeight = useCallback((): number => {
    if (typeof window === 'undefined') return 0

    const headerHeight = document.querySelector('header')?.offsetHeight ?? 0
    return headerHeight
  }, [])
  const handleScrollToProductList = useCallback(() => {
    if (typeof window !== 'undefined' && listProductRef?.current) {
      const headerHeight = getHeaderHeight()
      window.scrollTo({
        top: (listProductRef.current?.offsetTop || 0) - headerHeight,
        behavior: 'smooth',
      })
    }
  }, [getHeaderHeight])

  return (
    <>
      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid
          direction={'row'}
          flexWrap={'wrap'}
          justifyContent="space-between"
          alignItems={'center'}
          gap={2}
          size={{ xs: 12, xl: 3, xxl: 2.5 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              position: 'sticky',
              top: '0',
            }}
          >
            <FilterButton
              aggregations={savedAggregations || listProduct?.aggregations}
              isLoading={isPendingListProducts}
              onFilterApplied={handleScrollToProductList}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, xl: 9, xxl: 9.5 }} ref={listProductRef}>
          <Stack gap={1}>
            <Stack gap={2}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack
                  direction={'row'}
                  flexWrap={'wrap'}
                  justifyContent="end"
                  alignItems={'center'}
                  gap={2}
                  flex={1}
                >
                  <ProductSort />
                </Stack>
              </Stack>
              <ProductFilterTag listProduct={listProduct} />
            </Stack>
            {listProductItems()}
          </Stack>
        </Grid>
      </Grid>
      <DescriptionCategory description={checkRouter?.route?.description} />
    </>
  )
}
